/**
 * Each chunk captured by MediaRecorder is stored in IndexedDB parallel with
 * being streamed over the WebSocket, so a dropped connection, a socket
 * that never connects at all, or a crash does not lose captured audio.
 * Chunks are grouped by `localRecordingId` — a client-generated id created
 * the moment recording starts. This is the only identifier guaranteed to exist
 * for every chunk from the very first one, since the backend-assigned session
 * id may never arrive (e.g. the socket never connects).
 * Once the backend confirms a recording's audio stored in S3, its chunk records
 * are evicted by `localRecordingId`. Chunks that are never confirmed are left in
 * place for future retry/recovery, which is also why each record carries a `streamed`
 * flag and an `encounterId` index — so a future recovery pass can find every
 * unfinished recording for a given encounter and know which of its chunks
 * still need to be (re)sent.
 */

import {
  WOUNDSCRIBE_AUDIO_CHUNK_DB_NAME,
  WOUNDSCRIBE_AUDIO_CHUNK_DB_VERSION,
  WOUNDSCRIBE_AUDIO_CHUNK_ENCOUNTER_INDEX,
  WOUNDSCRIBE_AUDIO_CHUNK_ENCOUNTER_INDEX_KEY_PATH,
  WOUNDSCRIBE_AUDIO_CHUNK_INDEX_PAD_LENGTH,
  WOUNDSCRIBE_AUDIO_CHUNK_RECORDING_INDEX,
  WOUNDSCRIBE_AUDIO_CHUNK_RECORDING_INDEX_KEY_PATH,
  WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME,
} from 'woundscribe/core/constants';

export type AudioChunkRecord = {
  /** Unique, traceable, order-preserving key: `${localRecordingId}:${paddedChunkIndex}`. */
  key: string;
  /** Client-generated id for one continuous take; exists before any backend round-trip. */
  localRecordingId: string;
  /** Backend-assigned session id, filled in once/if start-session acks. Null while offline. */
  backendSessionId: string | null;
  encounterId: string;
  chunkIndex: number;
  blob: Blob;
  mimeType: string;
  /** Whether this chunk has been successfully emitted over the WebSocket. */
  streamed: boolean;
  createdAt: string;
};

let dbOpenPromise: Promise<IDBDatabase> | null = null;

function isIndexedDbAvailable(): boolean {
  // Temporarily disabled: no chunks are written, read, or evicted until this is restored.
  // return typeof indexedDB !== 'undefined';
  return false;
}

function openAudioChunkDb(): Promise<IDBDatabase> {
  if (dbOpenPromise) {
    return dbOpenPromise;
  }

  dbOpenPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(
      WOUNDSCRIBE_AUDIO_CHUNK_DB_NAME,
      WOUNDSCRIBE_AUDIO_CHUNK_DB_VERSION
    );

    request.onupgradeneeded = () => {
      const db = request.result;
      if (db.objectStoreNames.contains(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME)) {
        return;
      }
      const store = db.createObjectStore(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME, {
        keyPath: 'key',
      });
      store.createIndex(
        WOUNDSCRIBE_AUDIO_CHUNK_RECORDING_INDEX,
        WOUNDSCRIBE_AUDIO_CHUNK_RECORDING_INDEX_KEY_PATH
      );
      store.createIndex(
        WOUNDSCRIBE_AUDIO_CHUNK_ENCOUNTER_INDEX,
        WOUNDSCRIBE_AUDIO_CHUNK_ENCOUNTER_INDEX_KEY_PATH
      );
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      reject(
        request.error ?? new Error('Failed to open WoundScribe IndexedDB.')
      );
    };
  });

  // Do not cache a failed open — the next write/delete should retry.
  dbOpenPromise.catch(() => {
    dbOpenPromise = null;
  });

  return dbOpenPromise;
}

export function buildAudioChunkKey(
  localRecordingId: string,
  chunkIndex: number
): string {
  const paddedChunkIndex = String(chunkIndex).padStart(
    WOUNDSCRIBE_AUDIO_CHUNK_INDEX_PAD_LENGTH,
    '0'
  );
  return `${localRecordingId}:${paddedChunkIndex}`;
}

async function runReadWriteTransaction(
  work: (store: IDBObjectStore) => void
): Promise<void> {
  const db = await openAudioChunkDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME, 'readwrite');
    work(tx.objectStore(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME));
    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      reject(tx.error ?? new Error('Failed to readwrite to IndexedDB.'));
    };
    tx.onabort = () => {
      reject(tx.error ?? new Error('Abort readwrite to IndexedDB.'));
    };
  });
}

/**
 * Persists one audio chunk. Callers should not await this on the
 * streaming hot path — fire-and-forget, and log rather than throw so a
 * durability failure never interrupts recording.
 */
export async function putAudioChunkRecord(
  record: AudioChunkRecord
): Promise<void> {
  if (!isIndexedDbAvailable()) {
    return;
  }
  await runReadWriteTransaction((store) => store.put(record));
}

/**
 * Flags a chunk as successfully streamed, so a future retry pass only
 * resends chunks that never made it over the WebSocket.
 */
export async function markAudioChunkStreamed(key: string): Promise<void> {
  if (!isIndexedDbAvailable()) {
    return;
  }

  const db = await openAudioChunkDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME, 'readwrite');
    const store = tx.objectStore(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME);
    const getRequest = store.get(key);

    getRequest.onsuccess = () => {
      const record = getRequest.result as AudioChunkRecord | undefined;
      if (record && !record.streamed) {
        store.put({ ...record, streamed: true });
      }
    };
    getRequest.onerror = () => {
      reject(
        getRequest.error ??
          new Error('Failed to get chunk from IndexedDB to mark as streamed.')
      );
    };

    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      reject(tx.error ?? new Error('Failed to mark audio chunk streamed.'));
    };
    tx.onabort = () => {
      reject(tx.error ?? new Error('Abort marking audio chunk streamed.'));
    };
  });
}

/** Deletes every chunk record belonging to one recording take. */
export async function deleteAudioChunkRecordsForRecording(
  localRecordingId: string
): Promise<void> {
  if (!isIndexedDbAvailable()) {
    return;
  }

  await runReadWriteTransaction((store) => {
    const index = store.index(WOUNDSCRIBE_AUDIO_CHUNK_RECORDING_INDEX);
    const cursorRequest = index.openCursor(IDBKeyRange.only(localRecordingId));

    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) {
        return;
      }
      cursor.delete();
      cursor.continue();
    };
  });
}

/** Retrieves every stored chunk for a recording take, ordered by chunkIndex — for future retry/recovery use. */
export async function getAudioChunkRecordsForRecording(
  localRecordingId: string
): Promise<AudioChunkRecord[]> {
  if (!isIndexedDbAvailable()) {
    return [];
  }

  const db = await openAudioChunkDb();
  const records = await new Promise<AudioChunkRecord[]>((resolve, reject) => {
    const tx = db.transaction(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME, 'readonly');
    const store = tx.objectStore(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME);
    const index = store.index(WOUNDSCRIBE_AUDIO_CHUNK_RECORDING_INDEX);
    const request = index.getAll(IDBKeyRange.only(localRecordingId));

    request.onsuccess = () => resolve(request.result as AudioChunkRecord[]);
    request.onerror = () => {
      reject(
        request.error ??
          new Error('Failed to get audio chunks recordings by localRecordingId')
      );
    };
  });

  return records.sort((a, b) => a.chunkIndex - b.chunkIndex);
}

/**
 * Returns the distinct `localRecordingId`s that have chunks stored for a
 * given encounter — e.g. to power a future "resume unfinished recording"
 * prompt. An encounter can have more than one unfinished local recording
 * (a retake left over from a dropped connection), which is exactly why
 * this returns a list rather than a single id.
 */
export async function getAudioChunkRecordingIdsForEncounter(
  encounterId: string
): Promise<string[]> {
  if (!isIndexedDbAvailable()) {
    return [];
  }

  const db = await openAudioChunkDb();
  const records = await new Promise<AudioChunkRecord[]>((resolve, reject) => {
    const tx = db.transaction(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME, 'readonly');
    const store = tx.objectStore(WOUNDSCRIBE_AUDIO_CHUNK_STORE_NAME);
    const index = store.index(WOUNDSCRIBE_AUDIO_CHUNK_ENCOUNTER_INDEX);
    const request = index.getAll(IDBKeyRange.only(encounterId));

    request.onsuccess = () => resolve(request.result as AudioChunkRecord[]);
    request.onerror = () => {
      reject(
        request.error ??
          new Error('Failed to get audio chunks recordings by encounterId')
      );
    };
  });

  return Array.from(new Set(records.map((record) => record.localRecordingId)));
}
